import { Scene } from '@babylonjs/core';
import { Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Node } from '@babylonjs/core/node';

import {
  HEIGHT_CONTROL_PANEL_INITIAL_POSITION,
  HEIGHT_CONTROL_PANEL_UUID,
  HEIGHT_CONTROL_PANEL_WIDTH,
} from '../../constants';
import { Alignment } from '../../interfaces/Alignment';
import { Article } from '../../interfaces/Article';
import { Dimensions } from '../../interfaces/Dimensions';
import { ReplacementChild } from '../../interfaces/ReplacementChild';
import { Slot, SlotLine, SlotLineChild } from '../../interfaces/Slot';
import {
  isSlotLineDefinition,
  SlotDefinition,
  SlotLineDefinition,
  SlotPairDefinition,
  SlotType,
} from '../../interfaces/SlotDefinition';
import { ARTICLE_CHILD_NODE_NAME } from '../nodes/articleChildNode';
import ArticleNode from '../nodes/articleNode';
import SlotLineBehavior, { SLOT_LINE_BEHAVIOR_NAME } from '../nodes/slotLine/slotLineBehavior';
import MeshUtils from '../utils/meshUtils';
import { convertToWholeMillimeters, createSlotNodeName, SlotNodeVariant } from '../utils/nodeUtils';

export interface SlotLineUtil {
  displayLineSlot3DEndCaps: (slot: SlotLine) => void;
  clearSlotLineEndCaps: () => void;
  setNewSlotLineArrangement: (slot: SlotLine, newArrangement: SlotLineChild[]) => void;
}

export default class ArticleController implements SlotLineUtil {
  private static SLOT_LINE_CAP = 'slotLineCap';

  private readonly scene: Scene;
  private rootArticleNode: string;
  articleNodes: ArticleNode[] = [];
  heightControlPanel?: AbstractMesh;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  public getArticleNode(id: string | undefined): ArticleNode | undefined {
    if (!id) {
      return undefined;
    }
    return this.articleNodes.find((article) => article.id === id);
  }

  public getMesh(id: string | undefined): AbstractMesh | undefined {
    if (!id) {
      return undefined;
    }
    return this.articleNodes.find((article) => article.id === id)?.mesh || this.heightControlPanel;
  }

  public getRootArticleNode = () =>
    this.articleNodes.find((article) => article.id === this.rootArticleNode);

  public getSlotNode(
    parentId: string,
    definition: SlotDefinition,
    variant?: SlotNodeVariant,
  ): Node {
    const slotName = createSlotNodeName(definition, variant);
    const articleNode = this.articleNodes.find((node) => node.id === parentId);
    const slotNodes = articleNode.getSlotNodesByName(slotName);
    if (slotNodes.length === 0) {
      return null;
    }

    if (slotNodes.length > 1) {
      console.warn(
        `There are multiple nodes ${slotName} in parent ${parentId}, taking the first one`,
      );
    }

    return slotNodes[0];
  }

  public getAttachedOnSlotLineNode = (mesh: AbstractMesh): SlotLineBehavior | null => {
    const slotNode = mesh.parent;
    return slotNode && (slotNode.getBehaviorByName(SLOT_LINE_BEHAVIOR_NAME) as SlotLineBehavior);
  };

  public getSlotLineNode = (parentId: string, definition: SlotLineDefinition): SlotLineBehavior => {
    const slotNode = this.getSlotNode(parentId, definition, SlotNodeVariant.START);
    const result = slotNode.getBehaviorByName(SLOT_LINE_BEHAVIOR_NAME) as SlotLineBehavior;
    return result;
  };

  public addArticle(article: ArticleNode, slot: Slot) {
    this.articleNodes.push(article);

    switch (slot.type) {
      case SlotType.PAIR:
        return this.addPairSlotArticle(article, slot.parentId, slot.definition);
      case SlotType.LINE:
        return this.addLineSlotArticle(article, slot);
      case SlotType.SINGLE:
        return this.positionArticleOnSlot(article, slot.parentId, slot.definition);
    }

    throw new Error(`Slot ${(slot as SlotDefinition).name} has unknown slot type`);
  }

  addRootArticle(article: ArticleNode, positionOfHeightControlPanel?: number) {
    this.articleNodes.push(article);
    this.rootArticleNode = article.id;

    if (article.hasHeightControlPanel) {
      this.positionHeightControlPanel(
        article,
        positionOfHeightControlPanel || HEIGHT_CONTROL_PANEL_INITIAL_POSITION,
      );
    }
  }

  private addLineSlotArticle(article: ArticleNode, slot: SlotLine) {
    const slotLineNode = this.getSlotLineNode(slot.parentId, slot.definition);
    const position = slot.children.find((child) => child.id === article.id).position;
    slotLineNode.add(article.id, article.width, article.mesh, position);
  }

  private addPairSlotArticle(
    article: ArticleNode,
    parentId: string,
    definition: SlotPairDefinition,
  ) {
    this.positionArticleOnSlot(article, parentId, definition, SlotNodeVariant.FIRST);

    const pairedMeshes = article.mesh.getChildTransformNodes(true, (node) =>
      node.name.endsWith(Alignment.RIGHT),
    );

    const secondaryNode = pairedMeshes[0];
    const secondSlotNode = this.getSlotNode(parentId, definition, SlotNodeVariant.SECOND);
    if (secondaryNode) {
      secondaryNode.setParent(secondSlotNode);
      secondaryNode.setPositionWithLocalVector(Vector3.Zero());
      article.pairedNode = secondaryNode as AbstractMesh;
    }
  }

  public positionArticleOnSlot = (
    article: ArticleNode,
    parentId: string,
    definition: SlotDefinition,
    variant?: SlotNodeVariant,
  ) => {
    const slotNode = this.getSlotNode(parentId, definition, variant);
    const parentArticle = this.getArticleNode(parentId);

    if (definition.type === SlotType.SINGLE) {
      if (
        (definition.alignment === Alignment.LEFT && article.isFlipped) ||
        (definition.alignment === Alignment.RIGHT && !article.isFlipped) ||
        (parentArticle.isFlipped && !article.isFlipped)
      ) {
        article.flipMesh();
      }
    }

    article.mesh.setParent(slotNode);
    article.mesh.setPositionWithLocalVector(Vector3.Zero());
  };

  public removeArticle(id: string) {
    const articleNode = this.getArticleNode(id);
    this.articleNodes = this.articleNodes.filter((article) => article.id !== id);
    const slotLineNode = this.getAttachedOnSlotLineNode(articleNode.mesh);

    if (slotLineNode) {
      slotLineNode.remove(articleNode.id);
    }

    articleNode.dispose();
  }

  public moveOnLineSlot(id: string, position: number) {
    const node = this.getMesh(id);
    const slotLineNode = this.getAttachedOnSlotLineNode(node);
    slotLineNode.moveOnPosition(id, position);
  }

  public displayLineSlot3DEndCaps(slot: SlotLine): void {
    this.clearSlotLineEndCaps();

    const slotLineNode = this.getSlotLineNode(slot.parentId, slot.definition);
    const start = slotLineNode.startNode.getAbsolutePosition();
    const end = slotLineNode.endNode.getAbsolutePosition();

    const name = ArticleController.SLOT_LINE_CAP + '-' + slot.id;
    MeshUtils.createLineCaps(name, start, end, this.scene);
  }

  public clearSlotLineEndCaps() {
    const endCaps = this.scene.meshes.filter((child) =>
      child.name.startsWith(ArticleController.SLOT_LINE_CAP),
    );
    endCaps && endCaps.length > 0 && endCaps.forEach((a) => a.dispose());
  }

  public moveChildren(
    oldArticleId: string,
    newArticle: Article,
    children: ReplacementChild[],
  ): void {
    const oldArticleNode = this.getArticleNode(oldArticleId);
    const oldSlots = oldArticleNode.slots;

    children.forEach((child) => {
      const { id: childId, slot } = child;
      const childArticleNode = this.getArticleNode(childId);

      if (slot.type === SlotType.SINGLE) {
        this.positionArticleOnSlot(childArticleNode, newArticle.id, slot);
        return;
      }

      if (slot.type === SlotType.PAIR) {
        const oldPairSlotNodeFirst = this.getSlotNode(
          oldArticleNode.id,
          slot,
          SlotNodeVariant.FIRST,
        );
        const childNode = oldPairSlotNodeFirst?.getChildren(() => true, true)[0];
        const childArticleNode = this.getArticleNode(childNode?.name);
        if (!childArticleNode) {
          return;
        }

        childArticleNode.mergePairedMeshes();

        this.addPairSlotArticle(childArticleNode, newArticle.id, slot);
        return;
      }
    });

    oldSlots.filter(isSlotLineDefinition).forEach((slot) => {
      const oldSlotLineNode = this.getSlotLineNode(oldArticleNode.id, slot);
      const newSlotLineNode = this.getSlotLineNode(newArticle.id, slot);

      if (!oldSlotLineNode || !newSlotLineNode) {
        console.warn(
          `Search for slot line node on ${oldArticleNode.id} & ${newArticle.id} and found only ${oldSlotLineNode} & ${newSlotLineNode}.`,
        );
        return;
      }

      oldSlotLineNode.children.forEach((child) => {
        if (child.id !== HEIGHT_CONTROL_PANEL_UUID) {
          newSlotLineNode.add(child.id, child.requiredSpace, child.mesh, child.position);
        }
      });

      return;
    });
  }

  public calculateDimensions(): Dimensions {
    const root = this.getRootArticleNode();
    const { min, max } = root.mesh.getHierarchyBoundingVectors(true);
    return {
      width: convertToWholeMillimeters(max.z - min.z),
      height: convertToWholeMillimeters(max.y - min.y),
      depth: convertToWholeMillimeters(max.x - min.x),
    };
  }

  public positionHeightControlPanel(article: ArticleNode, position: number) {
    const HEIGHT_CONTROL_PANEL_MESH_NAME = 'heightcontrol';
    const mesh = article.allMeshes.find((mesh) =>
      mesh.name.includes(HEIGHT_CONTROL_PANEL_MESH_NAME),
    );
    if (!mesh) {
      console.warn(`Could not find mesh ${HEIGHT_CONTROL_PANEL_MESH_NAME} in article.`);
      return;
    }
    mesh.removeBehavior(mesh.getBehaviorByName(ARTICLE_CHILD_NODE_NAME));
    this.heightControlPanel = new AbstractMesh(HEIGHT_CONTROL_PANEL_UUID, this.scene);
    this.heightControlPanel.setAbsolutePosition(mesh.getAbsolutePosition());
    this.heightControlPanel.addChild(mesh);

    const slotLineNode = this.getSlotLineNode(
      article.id,
      article.slots.find((slot) => slot.type === SlotType.LINE) as SlotLineDefinition,
    );
    slotLineNode.add(
      HEIGHT_CONTROL_PANEL_UUID,
      HEIGHT_CONTROL_PANEL_WIDTH,
      this.heightControlPanel,
      position,
    );
  }

  public setNewSlotLineArrangement(slot: SlotLine, newArrangement: SlotLineChild[]) {
    const slotLineNode = this.getSlotLineNode(slot.parentId, slot.definition);
    slotLineNode.setNewArrangement(newArrangement);
  }

  public setThemePreview(material: string, color: string): void {
    this.articleNodes.forEach((node) => node.applyColor(material, color));
  }

  public resetThemePreview(): void {
    this.articleNodes.forEach((node) => node.applySelectedColors());
  }

  public applyTheme(material: string, color: string): void {
    this.articleNodes.forEach((node) => node.updateColor(material, color));
  }

  public cleanUpArticles(articleIds: string[]) {
    this.articleNodes
      .map((node) => node.id)
      .filter((id) => !articleIds.includes(id))
      .forEach((id) => this.removeArticle(id));
  }

  public reset() {
    this.rootArticleNode && this.scene.removeMesh(this.getRootArticleNode().mesh, true);
    this.articleNodes = [];
    this.rootArticleNode = undefined;
    this.heightControlPanel = undefined;
  }
}
